import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { showToast } from '../toastManager';
// Self-contained dialog kit (no Cognitive Flows dependencies) — reused so the
// Help form looks like every other dialog in the product.
import { Modal, Button, focusRing } from '../cognitive-flows/components/shared/Modal';

// The floating Support entry point, mounted once in App.js so it appears on
// every signed-in page. The bubble opens a help card: a greeting header, a
// searchable list of the most-asked questions (deep links into the public
// support portal), and a Home/Help footer where Help opens the report-an-issue
// form whose submission lands in the support inboxes + Slack via
// POST /api/support/issue.

const SUPPORT_URL = 'https://www.cognitiveux.ai/support';
// Dashboard brand colours. This widget renders on legacy pages too, where
// cognitive-flows/styles/global.css is not guaranteed to be loaded, so every
// colour carries its classic-blue value as the fallback; the theme tokens
// (theme/themeTokens.css, loaded globally, defined only in Green) turn the
// bubble and the card header the sidebar's Forest there (2026-09-25).
const PRIMARY = 'var(--cux-sidebar-bg, #132848)';
const PRIMARY_HOVER = 'var(--cux-sidebar-trigger, #1a3560)';
const ACCENT = 'var(--cux-accent, #2563eb)';
const ACCENT_LIGHT = 'var(--cux-accent-light, #eff6ff)';
const MAX_MESSAGE_CHARS = 5000;

const FAQS = [
    { q: 'How do I build a live dashboard with Canvas?', href: `${SUPPORT_URL}#canvas` },
    { q: 'How do I invite my team and set roles?', href: `${SUPPORT_URL}#workspaces-and-members` },
    { q: 'How do credits work?', href: `${SUPPORT_URL}#glossary` },
    { q: 'How do I add a ready-made workflow from the Library?', href: `${SUPPORT_URL}#workflow-library` },
];

const s = {
    // Bottom-right corner belongs to Support (the Cognitive Flows run-queue
    // widget moved to right:88 to make room). Above the run queue (1050),
    // below the sidebar (2000) — the card never needs to cover navigation.
    fab: {
        position: 'fixed', right: 24, bottom: 24, zIndex: 1200,
        width: 52, height: 52, borderRadius: '50%',
        background: PRIMARY, color: '#fff', border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.3)',
    },
    card: {
        position: 'fixed', right: 24, bottom: 86, zIndex: 1200,
        width: 360, maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'min(560px, calc(100vh - 120px))',
        display: 'flex', flexDirection: 'column',
        background: '#fff', borderRadius: 18, overflow: 'hidden',
        border: '1px solid #e2e8f0', boxShadow: '0 18px 50px rgba(15, 23, 42, 0.25)',
    },
    header: {
        position: 'relative', flexShrink: 0, padding: '20px 20px 16px',
        background: `linear-gradient(160deg, ${PRIMARY}, ${PRIMARY_HOVER})`, color: '#fff',
    },
    headerClose: {
        position: 'absolute', top: 12, right: 12,
        width: 30, height: 30, borderRadius: 8,
        border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.85)',
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, lineHeight: 1,
    },
    greetSmall: { margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.3 },
    greetBig: { margin: '2px 0 0', fontSize: 22, fontWeight: 750, lineHeight: 1.25, color: 'rgba(255,255,255,0.96)' },
    body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 14px 10px', background: '#fff' },
    search: {
        width: '100%', boxSizing: 'border-box', padding: '11px 13px',
        borderRadius: 10, border: '1px solid #e2e8f0', background: '#f1f5f9',
        fontSize: 13.5, fontFamily: 'inherit', color: '#0f172a', outline: 'none',
    },
    faqList: { display: 'flex', flexDirection: 'column', marginTop: 8 },
    faqItem: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '12px 10px', borderRadius: 10, textDecoration: 'none',
        color: '#0f172a', fontSize: 13.5, fontWeight: 550, lineHeight: 1.4,
    },
    faqChevron: { color: ACCENT, flexShrink: 0, fontSize: 15, lineHeight: 1 },
    faqEmpty: { padding: '14px 10px', fontSize: 13, color: '#64748b' },
    browseAll: {
        display: 'inline-block', margin: '6px 10px 8px', fontSize: 12.5, fontWeight: 600,
        color: ACCENT, textDecoration: 'none',
    },
    tabbar: { flexShrink: 0, display: 'flex', borderTop: '1px solid #e2e8f0', background: '#fff' },
    tab: (active) => ({
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        padding: '10px 8px 12px', border: 'none', background: 'none', cursor: 'pointer',
        fontSize: 12.5, fontWeight: 650, fontFamily: 'inherit',
        color: active ? ACCENT : '#64748b',
    }),
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: PRIMARY, margin: '14px 0 6px' },
    textarea: {
        width: '100%', minHeight: 130, padding: '10px 12px', borderRadius: 8,
        border: '1px solid #e2e8f0', fontSize: 13.5, fontFamily: 'inherit',
        outline: 'none', resize: 'vertical', boxSizing: 'border-box', color: '#0f172a',
    },
    fileRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    preview: { maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' },
    hint: { fontSize: 12, color: '#64748b', margin: '6px 0 0' },
};

/** The FAB glyph: a filled speech bubble with a smile, per the brand art. */
function ChatBubbleIcon({ size = 26 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="currentColor" />
            {/* The stroke goes through `style`: an SVG attribute cannot resolve a CSS variable. */}
            <path d="M8.4 10.2c1 1.1 2.2 1.6 3.6 1.6s2.6-.5 3.6-1.6" fill="none" style={{ stroke: PRIMARY }} strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

function HomeIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10.5 12 3l9 7.5" />
            <path d="M5.5 9.5V20a1 1 0 0 0 1 1H10v-5.5h4V21h3.5a1 1 0 0 0 1-1V9.5" />
        </svg>
    );
}

function HelpIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.4 9.3a2.7 2.7 0 0 1 5.3.9c0 1.8-2.7 2.3-2.7 3.6" />
            <circle cx="12" cy="17" r="0.4" fill="currentColor" />
        </svg>
    );
}

export default function SupportWidget({ app_url }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [message, setMessage] = useState('');
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [sending, setSending] = useState(false);
    const wrapRef = useRef(null);
    const fileInputRef = useRef(null);

    // The card closes on any outside click or Escape — standard popover manners.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => {
        if (!previewUrl) return undefined;
        return () => URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const pickFile = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (!/\.(png|jpe?g|webp|gif)$/i.test(f.name)) {
            showToast.error('Attach a PNG, JPG, WEBP, or GIF image.');
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            showToast.error('Screenshot is too large — the maximum size is 10 MB.');
            return;
        }
        setFile(f);
        setPreviewUrl(URL.createObjectURL(f));
    };

    const clearFile = () => { setFile(null); setPreviewUrl(null); };

    const closeHelp = () => {
        if (sending) return;
        setHelpOpen(false);
        setMessage('');
        clearFile();
    };

    const submit = async () => {
        const text = message.trim();
        if (!text) { showToast.error('Describe the issue before sending.'); return; }
        setSending(true);
        try {
            const form = new FormData();
            form.append('message', text.slice(0, MAX_MESSAGE_CHARS));
            form.append('page', location.pathname || '');
            if (file) form.append('screenshot', file);
            await axios.post(`${app_url}/api/support/issue`, form, { withCredentials: true });
            showToast.success('Thanks — the support team has your request.');
            setHelpOpen(false);
            setMessage('');
            clearFile();
        } catch (err) {
            showToast.error(err.response?.data?.error || 'Could not send your request. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const q = query.trim().toLowerCase();
    const faqs = q ? FAQS.filter((f) => f.q.toLowerCase().includes(q)) : FAQS;

    return (
        // `display: contents`: this wrapper exists only so outside-click
        // detection has one node to test against. It is mounted as a direct
        // child of App's `.grid-container` (a CSS grid with ONE implicit row),
        // and a wrapper that generates a box auto-places into a SECOND row
        // that takes half the free space — every short page then stalled at a
        // fixed height when the browser was zoomed out. With no box of its
        // own, the fixed children are the only thing it puts on screen.
        <div ref={wrapRef} style={{ display: 'contents' }}>
            <button
                type="button"
                style={s.fab}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label="Support"
                title="Support"
                data-testid="support-fab"
                {...focusRing}
            >
                <ChatBubbleIcon />
            </button>

            {open && (
                <div style={s.card} role="dialog" aria-label="Support">
                    <div style={s.header}>
                        <button
                            type="button" style={s.headerClose} aria-label="Close support"
                            data-testid="support-close" onClick={() => setOpen(false)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            {...focusRing}
                        >
                            ✕
                        </button>
                        <p style={s.greetSmall}>Hey you <span aria-hidden="true">👋</span></p>
                        <p style={s.greetBig}>How can we help?</p>
                    </div>

                    <div style={s.body}>
                        <input
                            style={s.search}
                            type="search"
                            placeholder="Search for help"
                            aria-label="Search for help"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="support-search"
                        />
                        <div style={s.faqList}>
                            {faqs.map((f, i) => (
                                <a
                                    key={f.href} href={f.href} target="_blank" rel="noopener noreferrer"
                                    style={s.faqItem} data-testid={`support-faq-${i + 1}`}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = ACCENT_LIGHT; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    {...focusRing}
                                >
                                    <span>{f.q}</span>
                                    <span style={s.faqChevron} aria-hidden="true">›</span>
                                </a>
                            ))}
                            {faqs.length === 0 && (
                                <p style={s.faqEmpty}>No matching questions.</p>
                            )}
                            <a
                                href={SUPPORT_URL} target="_blank" rel="noopener noreferrer"
                                style={s.browseAll} data-testid="support-articles" {...focusRing}
                            >
                                Browse all help articles →
                            </a>
                        </div>
                    </div>

                    <div style={s.tabbar}>
                        <button type="button" style={s.tab(true)} aria-current="page" data-testid="support-home-tab" {...focusRing}>
                            <HomeIcon />
                            Home
                        </button>
                        <button
                            type="button" style={s.tab(false)} data-testid="support-help"
                            onClick={() => { setOpen(false); setHelpOpen(true); }}
                            {...focusRing}
                        >
                            <HelpIcon />
                            Help
                        </button>
                    </div>
                </div>
            )}

            {helpOpen && (
                <Modal
                    title="Report an issue"
                    width={520}
                    busy={sending}
                    onClose={closeHelp}
                    footer={(
                        <>
                            <Button variant="ghost" onClick={closeHelp} disabled={sending}>Cancel</Button>
                            <Button variant="primary" onClick={submit} disabled={sending} data-testid="support-submit">
                                {sending ? 'Sending…' : 'Send to support'}
                            </Button>
                        </>
                    )}
                >
                    <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                        Tell us what went wrong — the message goes straight to the CognitiveUX support team.
                    </p>
                    <label style={s.label} htmlFor="support-issue-text">What happened?</label>
                    <textarea
                        id="support-issue-text"
                        style={s.textarea}
                        maxLength={MAX_MESSAGE_CHARS}
                        placeholder="Describe the issue — what you did, what you expected, and what happened instead."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        data-testid="support-message"
                    />
                    <label style={s.label}>Screenshot (optional)</label>
                    <div style={s.fileRow}>
                        <Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={sending}>
                            {file ? 'Replace screenshot' : 'Attach screenshot'}
                        </Button>
                        {file && <Button variant="ghost" onClick={clearFile} disabled={sending}>Remove</Button>}
                        <input
                            ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif"
                            style={{ display: 'none' }} onChange={pickFile} data-testid="support-screenshot"
                        />
                    </div>
                    {previewUrl && <div style={{ marginTop: 10 }}><img src={previewUrl} alt="Screenshot preview" style={s.preview} /></div>}
                    <p style={s.hint}>PNG, JPG, WEBP or GIF, up to 10 MB. The current page is included automatically.</p>
                    <div style={{ height: 14 }} />
                </Modal>
            )}
        </div>
    );
}
